import { Pagination } from "@core/domain/models/pagination";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { FetchJustificationTemplatesUseCase } from "@project/domain/usecases/budget/fetch-justification-templates-usecase";
import { DeleteJustificationTemplateUseCase } from "@project/domain/usecases/budget/delete-justification-template.usecase";

export interface JustificationTemplateTable {
    id: number;
    name: string;
    description: string;
    version: number;
}

interface JustificationTemplatesTable {
    justificationTemplates: JustificationTemplateTable[];
    count: number;
}

@injectable()
export class JustificationTemplateListPageViewModel extends BaseViewModel {
    @observable initialLoading: boolean = true;
    @observable justificationTemplates: JustificationTemplateTable[] = [];
    @observable pagination: Pagination = new Pagination();

    constructor(
        @inject(FetchJustificationTemplatesUseCase)
        private readonly fetchJustificationTemplatesUseCase: FetchJustificationTemplatesUseCase,
        @inject(DeleteJustificationTemplateUseCase)
        private readonly deleteJustificationTemplateUseCase: DeleteJustificationTemplateUseCase,
    ) {
        super();
        makeObservable(this);
    }
    @action
    async deleteTemplate(templateId: number) {
        await this.deleteJustificationTemplateUseCase.execute(templateId);
        await this.fetchTemplates();
    }

    @computed
    get justificationTemplatesTable(): JustificationTemplatesTable {
        return {
            count: this.justificationTemplates.length,
            justificationTemplates: this.justificationTemplates,
        };
    }

    override async didMount(): Promise<void> {
        await this.fetchTemplates();
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    setPagination = (page: number, pageSize: number) => {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;
        this.fetchTemplates();
    };

    async fetchTemplates(): Promise<void> {
        const templates = await this.fetchJustificationTemplatesUseCase.execute();
        runInAction(() => {
            this.justificationTemplates = templates.map((t: any) => ({
                id: t.id,
                name: t.name,
                description: t.description ?? "",
                version: t.version ?? 1,
                visible: t.visible ?? true,
            }));
        });
    }
}
